import { API_BASE_URL } from "../config";
import { raiseApiError, type ApiError } from "../lib/api";
import { getTerminalToken } from "../utils/terminalAuth";

export interface AuthenticatedUser {
  id: string;
  email: string;
  tenantId: string;
  displayName: string;
  roles: string[];

  // Multi-Tenant Support (für Tenant-Switcher vorbereitet)
  availableTenants?: Array<{
    tenantId: string;
    tenantSlug: string;
    tenantName: string;
    roles: string[];
    isPersonal: boolean;
  }>;
}

export interface AuthSessionInfo {
  authenticated: boolean;
  user: AuthenticatedUser | null;
}

export interface AuthResponse {
  user: AuthenticatedUser;
  tokenVersion: number;
  tenantSlug?: string;
  // Tokens included for iOS Safari fallback (when cookies don't work)
  accessToken?: string;
  refreshToken?: string;
}

// Token storage keys
const ACCESS_TOKEN_KEY = "ia_access_token";
const REFRESH_TOKEN_KEY = "ia_refresh_token";

// In-memory token cache to prevent race conditions with localStorage
let memoryTokenCache: { accessToken: string; refreshToken: string } | null = null;

// Token storage helpers
export function storeTokens(accessToken: string, refreshToken: string): void {
  try {
    console.log("[storeTokens] Storing tokens:", {
      accessTokenLength: accessToken.length,
      refreshTokenLength: refreshToken.length,
      accessTokenPrefix: accessToken.substring(0, 50),
      refreshTokenPrefix: refreshToken.substring(0, 50)
    });

    // Store in memory cache FIRST (immediate availability)
    memoryTokenCache = { accessToken, refreshToken };
    console.log("[storeTokens] Tokens cached in memory");

    // Then persist to localStorage
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);

    // Verify storage
    const storedAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    console.log("[storeTokens] Verification:", {
      accessStored: storedAccess === accessToken,
      refreshStored: storedRefresh === refreshToken,
      storedAccessLength: storedAccess?.length,
      storedRefreshLength: storedRefresh?.length
    });
  } catch (err) {
    console.error("[Auth] Failed to store tokens in localStorage:", err);
  }
}

export function getStoredAccessToken(): string | null {
  // Check memory cache FIRST (highest priority)
  if (memoryTokenCache?.accessToken) {
    console.log("[getStoredAccessToken] Using token from memory cache");
    return memoryTokenCache.accessToken;
  }

  // Fallback to localStorage
  try {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    console.log("[getStoredAccessToken] Reading from localStorage:", {
      key: ACCESS_TOKEN_KEY,
      hasToken: !!token,
      tokenLength: token?.length,
      tokenPrefix: token?.substring(0, 30)
    });
    return token;
  } catch (err) {
    console.error("[getStoredAccessToken] Failed to read from localStorage:", err);
    return null;
  }
}

export function clearStoredTokens(): void {
  try {
    // Clear memory cache FIRST
    memoryTokenCache = null;
    console.log("[clearStoredTokens] Memory cache cleared");

    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (err) {
    console.error("[Auth] Failed to clear tokens from localStorage:", err);
  }
}

// Helper to create auth headers (fallback to Bearer token if available)
export function getAuthHeaders(): HeadersInit {
  // Check for terminal token FIRST (terminal mode takes precedence)
  const terminalToken = getTerminalToken();
  if (terminalToken) {
    console.log("[Auth] Using Terminal Bearer token from localStorage:", {
      tokenLength: terminalToken.length,
      tokenPrefix: terminalToken.substring(0, 50),
      tokenSuffix: terminalToken.substring(terminalToken.length - 20)
    });
    return {
      "Authorization": `Bearer ${terminalToken}`
    };
  }

  // Fallback to regular user token
  const token = getStoredAccessToken();
  if (token) {
    console.log("[Auth] Using Bearer token from localStorage (iOS Safari fallback):", {
      tokenLength: token.length,
      tokenPrefix: token.substring(0, 50),
      tokenSuffix: token.substring(token.length - 20)
    });
    return {
      "Authorization": `Bearer ${token}`
    };
  }
  console.log("[Auth] No token in localStorage, relying on cookies");
  return {};
}

export type LoginInput = {
  email: string;
  password: string;
};

export type RegisterInput = LoginInput & {
  displayName?: string;
};

export const DEFAULT_AUTH_MESSAGE = "Bitte melde dich an, um den Assistenten zu nutzen.";

/**
 * Check database connection health
 */
export async function checkDatabaseConnection(apiBaseUrl: string = API_BASE_URL): Promise<{ connected: boolean; error?: string }> {
  try {
    const response = await fetch(`${apiBaseUrl}/health`, {
      method: "GET",
      credentials: "include"
    });

    if (!response.ok) {
      return { connected: false, error: "Backend-Server nicht erreichbar" };
    }

    const data = await response.json();

    // Check if database is connected
    if (data.database?.status === "connected") {
      return { connected: true };
    } else {
      return {
        connected: false,
        error: data.database?.error || "Datenbankverbindung fehlgeschlagen"
      };
    }
  } catch (error) {
    console.error("[Auth] Database connection check failed:", error);
    return {
      connected: false,
      error: error instanceof Error ? error.message : "Netzwerkfehler beim Verbindungscheck"
    };
  }
}

export async function login(payload: LoginInput, apiBaseUrl: string = API_BASE_URL): Promise<AuthResponse> {
  const response = await fetch(`${apiBaseUrl}/api/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    await raiseApiError(response, `Login failed (${response.status})`);
  }

  const result = await response.json() as AuthResponse;

  // Store tokens in localStorage for iOS Safari fallback
  if (result.accessToken && result.refreshToken) {
    console.log("[Auth] Storing tokens in localStorage for iOS Safari compatibility");
    storeTokens(result.accessToken, result.refreshToken);
  }

  return result;
}

export async function register(payload: RegisterInput, apiBaseUrl: string = API_BASE_URL): Promise<AuthResponse> {
  const response = await fetch(`${apiBaseUrl}/api/auth/register`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    await raiseApiError(response, `Registration failed (${response.status})`);
  }

  return response.json() as Promise<AuthResponse>;
}

export async function fetchSession(apiBaseUrl: string = API_BASE_URL): Promise<AuthSessionInfo> {
  console.log("[fetchSession] Calling /api/auth/me at:", `${apiBaseUrl}/api/auth/me`);
  console.log("[fetchSession] Visible cookies:", document.cookie || "(none visible - httpOnly cookies are hidden)");

  const response = await fetch(`${apiBaseUrl}/api/auth/me`, {
    headers: getAuthHeaders(), // Add Bearer token if available
    credentials: "include"
  });

  console.log("[fetchSession] Response status:", response.status);

  if (!response.ok) {
    console.error("[fetchSession] Failed with status:", response.status);
    await raiseApiError(response, `Profilabfrage fehlgeschlagen (${response.status})`);
  }

  const sessionInfo = await response.json() as AuthSessionInfo;
  console.log("[fetchSession] Authenticated:", sessionInfo.authenticated, "| User:", sessionInfo.user?.email || "none");

  return sessionInfo;
}

export async function logout(apiBaseUrl: string = API_BASE_URL): Promise<void> {
  console.log("[Logout] Starting logout request to:", `${apiBaseUrl}/api/auth/logout`);
  console.log("[Logout] Visible cookies before logout:", document.cookie);
  console.log("[Logout] Note: httpOnly cookies are not visible in document.cookie but are sent with requests");

  // Clear tokens BEFORE logout request to prevent session fixation
  const headers = getAuthHeaders();
  clearStoredTokens();
  console.log("[Logout] Cleared stored tokens from localStorage (before request)");

  const response = await fetch(`${apiBaseUrl}/api/auth/logout`, {
    method: "POST",
    headers, // Use pre-captured headers (tokens already cleared)
    credentials: "include"
  });

  console.log("[Logout] Response status:", response.status);
  console.log("[Logout] Response headers:", Object.fromEntries(response.headers.entries()));
  console.log("[Logout] Set-Cookie header:", response.headers.get("set-cookie"));
  console.log("[Logout] Visible cookies after logout:", document.cookie);

  if (!response.ok) {
    console.error("[Logout] Logout failed with status:", response.status);
    await raiseApiError(response, `Logout fehlgeschlagen (${response.status})`);
  }

  const result = await response.json();
  console.log("[Logout] Logout response:", result);

  // Test: Sofort nach Logout prüfen ob Session wirklich weg ist
  console.log("[Logout] Testing session immediately after logout...");
  try {
    const testResponse = await fetch(`${apiBaseUrl}/api/auth/me`, {
      credentials: "include"
    });
    const testSession = await testResponse.json();
    console.log("[Logout] Session check after logout:", testSession);
    console.log("[Logout] Still authenticated:", testSession.authenticated);
    if (testSession.authenticated) {
      console.error("[Logout] ❌ PROBLEM: User is still authenticated after logout!");
      console.error("[Logout] This means cookies were not properly cleared or refresh token is still valid");
    } else {
      console.log("[Logout] ✅ Session properly cleared");
    }
  } catch (err) {
    console.error("[Logout] Error checking session after logout:", err);
  }
}

export type { ApiError };
